import {getCenter} from 'ol/extent';
import GeoJSON from 'ol/format/GeoJSON';
import {Circle} from 'ol/geom';
import {fromExtent as polygonFromExtent} from 'ol/geom/Polygon';
import {transformExtent} from 'ol/proj';
import {getArea} from 'ol/sphere';
import {GEO_PROJECTION, MAX_GEOMETRY_AREA} from '../constants/index.js';
import {trackEvent} from '@app/analytics';

const MAX_SIDE_LENGTH = Math.sqrt(MAX_GEOMETRY_AREA * 1e6);
const MAX_RADIUS = Math.sqrt((MAX_GEOMETRY_AREA * 1e6) / Math.PI);
const MIN_RADIUS = 10000;

function getMaxExtent(coords) {
  const half = MAX_SIDE_LENGTH / 2;
  return [
    coords[0] - half,
    coords[1] - half,
    coords[0] + half,
    coords[1] + half,
  ];
}

function getMinExtent(coords) {
  return [
    coords[0] - MIN_RADIUS,
    coords[1] - MIN_RADIUS,
    coords[0] + MIN_RADIUS,
    coords[1] + MIN_RADIUS,
  ];
}

export const MIN_AREA = Math.PI * MIN_RADIUS * MIN_RADIUS;

export function getMaxSquare(coords) {
  return polygonFromExtent(getMaxExtent(coords));
}

export function getMinSquare(coords) {
  return polygonFromExtent(getMinExtent(coords));
}

export function getMaxCircle(coords) {
  return new Circle(coords, MAX_RADIUS);
}

export function getMinCircle(coords) {
  return new Circle(coords, MIN_RADIUS);
}

export const bufferExtent = (extent, buffer) => {
  if (buffer > 0) {
    const w = (extent.right - extent.left) * buffer;
    const h = (extent.top - extent.bottom) * buffer;
    return {
      left: extent.left - w,
      right: extent.right + w,
      bottom: extent.bottom - h,
      top: extent.top + h,
    };
  }
  return extent;
};

export const getPointExtent = (geom, buffer = 0) => {
  const [x, y] = geom.coordinates;
  return [x - buffer, y - buffer, x + buffer, y + buffer];
};

export const getPolygonExtent = (geom, buffer = 0) => {
  const first = geom.coordinates[0][0];
  // copy and dereference
  let [minX, minY, maxX, maxY] = [first[0], first[1], first[0], first[1]].map(
    x => x + 0.0
  );

  geom.coordinates.forEach(ring => {
    ring.forEach(point => {
      const x = point[0];
      const y = point[1];
      if (x < minX) {
        minX = x;
      }
      if (y < minY) {
        minY = y;
      }
      if (x > maxX) {
        maxX = x;
      }
      if (y > maxY) {
        maxY = y;
      }
    });
  });

  return bufferExtent(
    {
      left: minX,
      bottom: minY,
      right: maxX,
      top: maxY,
    },
    buffer
  );
};

export const getMultiPolygonExtent = (geom, buffer = 0) => {
  const extents = geom.coordinates.map(poly =>
    getPolygonExtent({coordinates: poly})
  );
  const overallExtent = {
    left: Math.min.apply(
      null,
      extents.map(ext => ext.left)
    ),
    bottom: Math.min.apply(
      null,
      extents.map(ext => ext.bottom)
    ),
    right: Math.max.apply(
      null,
      extents.map(ext => ext.right)
    ),
    top: Math.max.apply(
      null,
      extents.map(ext => ext.top)
    ),
  };

  return bufferExtent(overallExtent, buffer);
};

export const getExtent = (geom, buffer = 0, toWebMerc = false) => {
  let extent = {};
  if (geom.type === 'Polygon') {
    extent = getPolygonExtent(geom, buffer);
  } else if (geom.type === 'LineString') {
    // nest the line string to look like a polygon
    extent = getPolygonExtent({coordinates: [geom.coordinates]});
  } else if (geom.type === 'Point') {
    extent = getPointExtent(geom, buffer);
  } else {
    // assuming multipolygon but this can be wrong...
    extent = getMultiPolygonExtent(geom, buffer);
  }

  if (toWebMerc) {
    const olExtent = transformExtent(
      [extent.left, extent.bottom, extent.right, extent.top],
      'EPSG:4326',
      'EPSG:3857'
    );
    extent.left = olExtent[0];
    extent.bottom = olExtent[1];
    extent.right = olExtent[2];
    extent.top = olExtent[3];
  }

  return extent;
};

export const extentToPolygon = extent => {
  // check the extent format
  let ext = extent;
  if (extent.left) {
    ext = [extent.left, extent.bottom, extent.right, extent.top];
  }

  return {
    type: 'Polygon',
    coordinates: [
      [
        [ext[0], ext[1]],
        [ext[0], ext[3]],
        [ext[2], ext[3]],
        [ext[2], ext[1]],
        [ext[0], ext[1]],
      ],
    ],
  };
};

const GEOJSON = new GeoJSON();
export const toGeoJSON = (geom, reproject = false) =>
  GEOJSON.writeGeometryObject(
    geom,
    reproject
      ? {featureProjection: 'EPSG:3857', dataProjection: 'EPSG:4326'}
      : undefined
  );

export const computeGeometryTooLarge = geometry => {
  const area = getArea(geometry, {projection: GEO_PROJECTION}) / 1e6;
  const geometryTooLarge = area / MAX_GEOMETRY_AREA >= 1.1;
  return {geometryTooLarge, area};
};

export const createGeometryObj = geometry => {
  let newGeometry;
  let geometryTooLarge = true;
  let center;
  const formatGeoJSON = new GeoJSON();

  if (geometry) {
    const olGeom = formatGeoJSON.readGeometry(geometry);
    newGeometry = olGeom.simplify(0.000001);
    const {geometryTooLarge: isGeometryTooLarge, area} =
      computeGeometryTooLarge(olGeom);
    geometryTooLarge = isGeometryTooLarge;

    if (geometryTooLarge) {
      trackEvent('Geometry defined - large AOI', {
        area,
        units: 'sqkm',
      });
    }

    center = getCenter(olGeom.getExtent());
    newGeometry = formatGeoJSON.writeGeometryObject(newGeometry, {decimals: 6});
  } else {
    newGeometry = geometry;
  }

  return {newGeometry, geometryTooLarge, center};
};
